import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { IUniteOrganisationnelle } from 'src/app/shared/models/up-evaluation';
import { UniteOrganisationnelleService } from '../uo-evaluation.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';

@Component({
  selector: 'app-uo-dialog',
  templateUrl: './uo-dialog.component.html',
  styleUrls: ['./uo-dialog.component.less']
})
export class UoDialogComponent implements OnInit {

  form!: UntypedFormGroup;
  isEditMode: boolean;
  isSubmitting = false;
  backendError: string | null = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private uoService: UniteOrganisationnelleService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<UoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IUniteOrganisationnelle | null
  ) {
    this.isEditMode = data != null;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.form = this._fb.group({
      code:    [this.data?.code ?? '',    [Validators.required, Validators.maxLength(50)]],
      libelle: [this.data?.libelle ?? '', [Validators.required, Validators.maxLength(150)]]
    });
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.backendError = null;
    this.isSubmitting = true;

    const { code, libelle } = this.form.value;

    const action$ = this.isEditMode
      ? this.uoService.update(this.data!.id, { code, libelle })
      : this.uoService.create({ code, libelle });

    const successMsg = this.isEditMode
      ? 'Unité organisationnelle modifiée avec succès'
      : 'Unité organisationnelle créée avec succès';

    const fallbackMsg = this.isEditMode
      ? 'Erreur lors de la modification'
      : 'Erreur lors de la création';

    action$.subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(successMsg);
        this.dialogRef.close(result);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, fallbackMsg);
      }
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  onDelete(): void {
    const confirmRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer l'UO "${this.data!.code} — ${this.data!.libelle}" ? Les compétences et évaluations associées perdront leur lien.` }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isSubmitting = true;
      this.uoService.delete(this.data!.id).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.snackbarService.openSnackBar('Unité organisationnelle supprimée avec succès');
          this.dialogRef.close('deleted');
        },
        error: (err) => {
          this.isSubmitting = false;
          this.backendError = this.extractErrorMessage(err, 'Erreur lors de la suppression');
        }
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}